import type { LucideIcon } from "lucide-react";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";

interface SidebarFrameItemProps {
  title: string;
  icon: LucideIcon;
  active?: boolean;
  statusColor?: string;
  hideAccent?: boolean;
  hidden?: boolean;
  onToggleVisibility?: () => void;
  onClick?: () => void;
  className?: string;
  "data-testid"?: string;
}

export function SidebarFrameItem({
  title,
  icon: Icon,
  active = false,
  statusColor = "bg-accent",
  hideAccent = false,
  hidden = false,
  onToggleVisibility,
  onClick,
  className,
  "data-testid": testId,
}: SidebarFrameItemProps) {
  const frameTestId =
    testId ??
    `sidebar-frame-${
      title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "") || "item"
    }`;

  return (
    <div
      className={cn(
        "group flex h-appkit-xl w-full items-center gap-appkit-xs px-appkit-md transition-colors",
        active ? "bg-glass-stroke-light" : "hover:bg-glass-stroke-subtle",
        hidden && "opacity-40",
        className,
      )}
      data-testid={frameTestId}
    >
      {!hideAccent && (
        <span
          className="h-appkit-sm w-[calc(var(--appkit-space-2xs)/2)] shrink-0 rounded-pill bg-accent"
          aria-hidden="true"
        />
      )}
      <button
        type="button"
        onClick={onClick}
        className="flex flex-1 items-center gap-appkit-xs overflow-hidden"
        data-testid={`${frameTestId}-button`}
      >
        <Icon
          className="h-appkit-sm w-appkit-sm shrink-0 text-muted"
          aria-hidden="true"
        />
        <span className="flex-1 truncate text-left text-appkit-small text-secondary">
          {title}
        </span>
      </button>
      {/* Eye toggle — visible on hover */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleVisibility?.();
        }}
        className="hidden h-appkit-md w-appkit-md shrink-0 items-center justify-center rounded-control-sm text-muted transition-colors hover:text-primary group-hover:flex"
        aria-label={hidden ? "Show frame" : "Hide frame"}
        data-testid={`${frameTestId}-visibility`}
      >
        {hidden ? (
          <Icons.EyeOff className="h-appkit-sm w-appkit-sm" />
        ) : (
          <Icons.Eye className="h-appkit-sm w-appkit-sm" />
        )}
      </button>
      <span
        className={cn(
          "h-appkit-2xs w-appkit-2xs shrink-0 rounded-pill group-hover:hidden",
          statusColor,
        )}
        aria-hidden="true"
      />
    </div>
  );
}

export type { SidebarFrameItemProps };
